import { Component, OnDestroy, OnInit, inject, ChangeDetectionStrategy } from '@angular/core';
import { forkJoin } from 'rxjs';

import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';

import { MenuCommonComponent } from '@app/shared/menu-common/menu-common.component';
import { HrService } from '../hr.service';
import { LoginService } from '@app/common/login.service';
import { HrClock, HrEmployee, hrEmployeeNameOf, hrIsHrManager, hrMyEmployeeId } from '@app/shared/_models/hr';
import { User } from '@app/shared/_models/common';
import { EVENTTYP_HR_CLOCK_PUNCH, HR_CLOCK_IN, HR_CLOCK_OUT, HR_CLOCK_SOURCE_KIOSK, OBJECTTYP_HR_CLOCK } from '@app/shared/constants';
import { businessDayOf, parseTz, todayStr } from '@app/shared/time';

interface KioskEmployee {
	id: number;
	name: string;
	// Les fiches et pointages des AUTRES sont expurgés pour une session non-responsable
	// (strip_employee force has_pin=false, keep_mine filtre les pointages) : hasPin, present et
	// since ne sont FIABLES que sur sa propre fiche ou sous une session responsable RH.
	trusted: boolean;
	hasPin: boolean;
	present: boolean;			// dernier pointage du jour = entrée (fiable si trusted)
	since: string | null;	// « 08:02 » heure du dernier pointage (fiable si trusted)
}

// Pointeuse KIOSQUE — /hr/timeclock. Tablette partagée au magasin : l'employé touche son nom,
// tape son PIN, pointe l'arrivée ou le départ. L'heure est TOUJOURS celle du serveur (event 2801) —
// l'horloge de la tablette ne fait pas foi (obligation d'un système fiable et infalsifiable).
//
// Quand le PLANNING fait foi (`time_source = 'planning'`, le défaut), pointer ne compte aucune
// heure : le bandeau le dit dès le premier écran et les deux boutons de pointage sont grisés —
// même règle que le bouton « Pointer » de la navbar. L'écran reste accessible pour qu'on voie
// l'effectif et où changer le réglage, pas pour enregistrer des pointages sans portée.
@Component({
	selector: 'app-hr-timeclock',
	standalone: true,
	imports: [MenuCommonComponent, MatButtonModule, MatIconModule],
	templateUrl: './timeclock.component.html',
	changeDetection: ChangeDetectionStrategy.Eager,
	styleUrl: './timeclock.component.scss',
})
export class TimeclockComponent implements OnInit, OnDestroy {

	public hrService = inject(HrService);
	private loginService = inject(LoginService);

	public step: 'pick' | 'pin' | 'done' | 'error' = 'pick';
	public kioskEmployees: KioskEmployee[] = [];
	public selected: KioskEmployee | null = null;
	public pin = '';
	public posting = false;
	public doneMessage = '';
	public errorMessage = '';
	public nowLabel = '';
	public clockMode = false;			// la POINTEUSE fait foi (time_source = 'clock')

	private employees: HrEmployee[] = [];
	private users: User[] = [];
	private clockTimer?: ReturnType<typeof setInterval>;
	private resetTimer?: ReturnType<typeof setTimeout>;

	ngOnInit(): void {
		this.tick();
		this.clockTimer = setInterval(() => this.tick(), 1000);
		this.load();
	}

	ngOnDestroy(): void {
		if (this.clockTimer) clearInterval(this.clockTimer);
		if (this.resetTimer) clearTimeout(this.resetTimer);
	}

	private tick(): void {
		const now = parseTz(new Date());
		this.nowLabel = now ? now.format('HH:mm:ss') : '';
	}

	private load(): void {
		const today = todayStr();
		// Le kiosque est CELUI D'UN MAGASIN : le serveur ne renvoie que les personnes qui peuvent y
		// travailler (rattachées ou mobiles). Le filtre de buildKiosk reste, il redevient un simple
		// garde-fou au lieu d'être la seule barrière — un kiosque ne doit pas lister l'organisation.
		forkJoin({
			employees: this.hrService.getEmployees$(this.loginService.currentstation?.store ?? undefined),
			users: this.hrService.getUsers$(),
			clocks: this.hrService.getClocks$(today, today),
			payrollRules: this.hrService.payrollRules$(),
		}).subscribe(({ employees, users, clocks, payrollRules }) => {
			this.employees = employees;
			this.users = users;
			this.clockMode = payrollRules.rules.time_source === 'clock';
			this.buildKiosk(clocks);
		});
	}

	private buildKiosk(clocks: HrClock[]): void {
		const store = this.loginService.currentstation?.store;
		const today = todayStr();
		// Le kiosque tourne sous la session de QUELQU'UN (souvent un simple salarié) : le serveur
		// expurge alors les fiches des autres (has_pin forcé à false) et filtre leurs pointages.
		// On ne fait donc confiance à ces données que sous session responsable RH, ou sur la
		// propre fiche du connecté — partout ailleurs, on n'affirme rien.
		const manager = hrIsHrManager(this.loginService.loggeduser, this.users);
		const mineId = hrMyEmployeeId(this.loginService.loggeduser, this.employees);
		this.kioskEmployees = this.employees
			.filter(e => e.is_active !== false)
			.filter(e => !store || e.id_store === store || (e.mobility ?? []).includes(store))
			.map(e => {
				const trusted = manager || e.id === mineId;
				const mine = clocks
					.filter(c => c.id_employee === e.id && !c.cancelled && businessDayOf(c.ts) === today)
					.sort((a, b) => (a.ts < b.ts ? -1 : 1));
				const last = mine[mine.length - 1];
				return {
					id: e.id,
					name: hrEmployeeNameOf(e, this.users),
					trusted,
					// Le code lui-même n'arrive plus jusqu'ici (write-only) : le serveur dit seulement
					// s'il en existe un. C'est LUI qui le vérifie, au pointage.
					hasPin: !!e.has_pin,
					present: trusted && last?.io === HR_CLOCK_IN,
					since: trusted && last ? (parseTz(last.ts)?.format('HH:mm') ?? null) : null,
				};
			})
			.sort((a, b) => a.name.localeCompare(b.name));
	}

	// ============================================================
	// Interaction kiosque
	// ============================================================

	onPick(e: KioskEmployee): void {
		// On ne bloque que si l'absence de PIN est un fait SÛR (fiche fiable) : pour les autres
		// fiches, has_pin est expurgé — on laisse essayer, le serveur tranche au pointage.
		if (e.trusted && !e.hasPin) return;
		this.selected = e;
		this.pin = '';
		this.errorMessage = '';
		this.step = 'pin';
	}

	onDigit(d: number): void {
		// La fiche accepte un code de 4 à 8 chiffres (maxlength=8, PIN_MIN_LEN=4 côté serveur) :
		// le pavé doit accepter la même chose — bcrypt compare la chaîne COMPLÈTE.
		if (this.pin.length >= 8) return;
		this.pin += d;
		this.errorMessage = '';
	}

	onClearPin(): void {
		this.pin = '';
		this.errorMessage = '';
	}

	onBackToPick(): void {
		this.step = 'pick';
		this.selected = null;
		this.pin = '';
		this.errorMessage = '';
		if (this.resetTimer) clearTimeout(this.resetTimer);
	}

	// L'action proposée en évidence suit l'état courant : présent ⇒ départ. Quand les pointages
	// de la fiche ne sont pas disponibles (session non-responsable, fiche d'un autre), on ne
	// suggère RIEN (0) : les deux boutons se présentent à égalité, l'employé sait ce qu'il fait.
	get suggestedIo(): number {
		if (!this.selected?.trusted) return 0;
		return this.selected?.present ? HR_CLOCK_OUT : HR_CLOCK_IN;
	}

	onPunch(io: number): void {
		if (!this.selected || this.pin.length < 4 || this.posting || !this.clockMode) return;
		this.posting = true;
		this.hrService.postHrEvent(EVENTTYP_HR_CLOCK_PUNCH, OBJECTTYP_HR_CLOCK, {
			id_employee: this.selected.id, pin: this.pin, io, source: HR_CLOCK_SOURCE_KIOSK,
		}).subscribe({
			next: (res) => {
				this.posting = false;
				const at = res?.data?.ts ? (parseTz(res.data.ts)?.format('HH:mm') ?? '') : '';
				this.doneMessage = (io === HR_CLOCK_IN ? 'Arrivée' : 'Départ') + ' de ' + this.selected!.name + ' enregistré' + (at ? ' à ' + at : '') + '.';
				this.step = 'done';
				this.pin = '';
				this.load();
				this.resetTimer = setTimeout(() => this.onBackToPick(), 4000);
			},
			// Refus métier (mauvais PIN — postHrEvent le convertit en erreur RxJS) comme vraie
			// panne : même geste, le PIN se vide et l'employé réessaie.
			error: () => { this.posting = false; this.failPunch(); },
		});
	}

	private failPunch(): void {
		this.errorMessage = 'Pointage refusé — vérifiez votre code PIN.';
		this.pin = '';
	}
}
